import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Search, Mail, Users, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSubscribers = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ count: 0, subscribers: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/subscribers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      toast.error('Error loading subscribers');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Email', 'Subscribed Date'],
      ...data.subscribers.map(s => [
        s.email,
        new Date(s.subscribed_at).toISOString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported!');
  };

  const filteredSubscribers = data.subscribers.filter(sub =>
    sub.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-terracota border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-subscribers">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-serif text-white mb-1">Newsletter Subscribers</h1>
          <p className="text-stone-400 text-sm">{data.count} active subscribers</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 bg-stone-700 text-white px-4 py-2 rounded-lg hover:bg-stone-600 transition-colors"
        >
          <Download className="w-5 h-5" />
          Export CSV
        </button>
      </div>

      {/* Stats Card */}
      <div className="bg-gradient-to-r from-terracota to-orange-600 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
            <Users className="w-8 h-8 text-white" />
          </div>
          <div>
            <p className="text-white/80 text-sm">Active Subscribers</p>
            <p className="text-4xl font-bold text-white">{data.count}</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
        <input
          type="text"
          placeholder="Search subscribers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-stone-800 border border-stone-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota"
        />
      </div>

      {/* Subscribers List */}
      <div className="bg-stone-800 rounded-xl border border-stone-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-700">
                <th className="text-left px-4 py-3 text-stone-400 text-sm font-medium">Email</th>
                <th className="text-left px-4 py-3 text-stone-400 text-sm font-medium">Subscribed Date</th>
                <th className="text-left px-4 py-3 text-stone-400 text-sm font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscribers.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-stone-400">
                    No subscribers found
                  </td>
                </tr>
              ) : (
                filteredSubscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="border-b border-stone-700/50 hover:bg-stone-700/30">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-terracota/20 rounded-full flex items-center justify-center">
                          <Mail className="w-4 h-4 text-terracota" />
                        </div>
                        <p className="text-white">{subscriber.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-stone-400 text-sm">
                        {new Date(subscriber.subscribed_at).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <a
                        href={`mailto:${subscriber.email}`}
                        className="p-2 text-stone-400 hover:text-terracota transition-colors inline-block"
                        title="Send email"
                      >
                        <Mail className="w-4 h-4" />
                      </a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminSubscribers;
